import { defaultError } from '@qpr/client-library';
import Attributes from '@qpr/map/components/Attributes';
import api, { ApiResult } from '../../api/api-caller';

interface Attribute {
  attribute: string;
  displayName: string;
}

interface AttributeInfo extends Attribute {
  _id: string;
  type: string;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
type Attributes = Attribute[];

interface AttributeStateData {
  activeAttributes: Array<AttributeInfo | null>;
  attributes: AttributeInfo[];
}

interface AttributesState {
  data: AttributeStateData;
  error?: { message: string; details: string };
}

interface AttributesViewModel {
  getAttributes: (attributes: Attribute[]) => Promise<AttributesState>;
}

const errorHandler = (apiResult: ApiResult): AttributesState => {
  return {
    data: { activeAttributes: [], attributes: [] },
    error: apiResult.error || defaultError
  };
};

const transformData = (
  urlAttributes: Attribute[],
  apiAttributes: AttributeInfo[]
): AttributeStateData => {
  const activeAttributes = apiAttributes.map(apiAttribute => {
    const foundAttributes = urlAttributes.filter(
      urlAttribute => urlAttribute.attribute === apiAttribute.attribute
    );

    const foundAttribute = foundAttributes.pop();

    if (foundAttribute) {
      const apiDisplayName = apiAttribute.displayName;
      const urlDisplayName = foundAttribute.displayName;

      let displayName =
        urlDisplayName.length === 0 || urlDisplayName === apiDisplayName
          ? apiDisplayName
          : `${urlDisplayName} (${apiDisplayName})`;

      if (foundAttribute.attribute.toLowerCase() === 'name') {
        let additionalName = '';
        if (urlDisplayName.length > 0 && urlDisplayName.toLowerCase() !== 'name')
          additionalName = urlDisplayName;
        if (
          urlDisplayName.length > 0 &&
          urlDisplayName.toLowerCase() !== 'name' &&
          apiDisplayName.length > 0 &&
          apiDisplayName.toLowerCase() !== 'name' &&
          apiDisplayName.toLowerCase() !== urlDisplayName.toLowerCase()
        )
          additionalName += ', ';
        if (
          apiDisplayName.length > 0 &&
          apiDisplayName.toLowerCase() !== 'name' &&
          apiDisplayName.toLowerCase() !== urlDisplayName.toLowerCase()
        )
          additionalName += apiDisplayName;
        displayName = additionalName.length === 0 ? 'Name' : `Name (${additionalName})`;
      }

      return {
        ...apiAttribute,
        displayName
      };
    }

    return null;
  });

  let activeNameAttribute = activeAttributes.filter(
    activeAttribute => activeAttribute?.attribute.toLowerCase() === 'name'
  )[0];

  const urlNameAttribute = urlAttributes.filter(
    urlAttribute => urlAttribute.attribute.toLowerCase() === 'name'
  )[0];

  const apiNameAttribute = apiAttributes.filter(
    apiAttribute => apiAttribute.attribute.toLowerCase() === 'name'
  )[0];

  const nameAttribute = urlNameAttribute || apiNameAttribute;

  if (nameAttribute && !activeNameAttribute) {
    const displayName =
      nameAttribute.displayName?.length === 0 || nameAttribute.displayName?.toLowerCase() === 'name'
        ? 'Name'
        : `Name (${nameAttribute.displayName})`;

    if (apiNameAttribute) apiNameAttribute.displayName = displayName;
    if (urlNameAttribute) urlNameAttribute.displayName = displayName;

    activeNameAttribute = {
      displayName,
      attribute: 'name',
      _id: 'name-attribute',
      type: 'text'
    };
    activeAttributes.push(activeNameAttribute);
  }

  const attributes = apiAttributes.filter(
    apiAttribute =>
      urlAttributes.filter(urlAttribute => urlAttribute.attribute === apiAttribute.attribute)
        .length === 0
  );

  return {
    activeAttributes: activeAttributes.filter(activeAttribute => activeAttribute !== null),
    attributes
  };
};

const fetchAttributes = async (urlAttributes: Attributes) => {
  const result = await api.asyncCallApi('attributeinfos', { method: 'GET' });
  if (!result.ok) errorHandler(result);
  const data = transformData(urlAttributes, result.data);
  return { data };
};

const viewModelCache: Record<string, AttributesState> = {};

const createAttributesViewModel = (): AttributesViewModel => {
  const getAttributes = async (attributes: Attributes): Promise<AttributesState> => {
    const cacheKey: string = attributes.toString();
    if (!viewModelCache[cacheKey]) viewModelCache[cacheKey] = await fetchAttributes(attributes);
    return viewModelCache[cacheKey];
  };

  return { getAttributes };
};

export default createAttributesViewModel;
